// Typed API client for the vaultd backend (§27, /api/v1). On the web it is same-origin;
// inside the Tauri desktop webview (origin tauri://) it targets the real backend host. The
// session token is held in memory only (never localStorage, §20) and sent as a Bearer header.

// Desktop webview talks to the configured backend; the web build stays same-origin (relative).
// The browser-extension service worker has no same-origin backend either — its build bakes
// the absolute origin in (VITE_IS_EXTENSION is defined only by vite.ext*.config.ts).
function detectBackendOrigin(): string {
  if (import.meta.env.VITE_IS_EXTENSION) {
    return (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "https://vault.itgrow.cz";
  }
  if (typeof window !== "undefined" && "__TAURI_INTERNALS__" in window) {
    return (import.meta.env.VITE_BACKEND_URL as string | undefined) ?? "https://vault.itgrow.cz";
  }
  return "";
}

/** Backend origin ("" on web = same-origin; absolute URL on desktop). */
export function backendOrigin(): string {
  return detectBackendOrigin();
}

import { useConnectivity } from "../net/connectivity";

const BASE = detectBackendOrigin() + "/api/v1";
// Build-time app version (from package.json via Vite); sent as X-Vault-Client-Version.
const CLIENT_VERSION = __APP_VERSION__;

/** Probe backend reachability with a short timeout (for online-first bootstrap). Resolves true if
 *  the server answers at all (any status), false on network error/timeout. Updates connectivity. */
export async function probeReachable(timeoutMs = 3500): Promise<boolean> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    useConnectivity.getState().markApiFailure();
    return false;
  }
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    // `no-cors`: the desktop bootstrap probes from the bundled origin (http://tauri.localhost),
    // which is cross-origin to the backend. We only need to know the server ANSWERED (opaque
    // response is fine) — a normal fetch would reject on the missing CORS header and falsely
    // report the server unreachable, forcing the app offline.
    await fetch(BASE + "/healthz", { mode: "no-cors", signal: ctrl.signal });
    useConnectivity.getState().markApiSuccess();
    return true;
  } catch {
    useConnectivity.getState().markApiFailure();
    return false;
  } finally {
    clearTimeout(id);
  }
}

let sessionToken: string | null = null;

export function setSessionToken(t: string | null) {
  sessionToken = t;
}

/** Current session token (for the native desktop bridge). */
export function getSessionToken(): string | null {
  return sessionToken;
}

export class ApiError extends Error {
  status: number;
  title: string;
  detail?: string;
  constructor(status: number, title: string, detail?: string) {
    super(detail || title);
    this.status = status;
    this.title = title;
    this.detail = detail;
  }
}

// Central API error policy. The app registers these once (see <ApiErrorPolicy/>, mounted inside the
// ToastProvider): a 401 clears the session so the UI returns to sign-in instead of silently rendering
// an empty vault, and a 5xx is surfaced so a silent local `catch {}` can't hide a server fault.
// Registration is optional — request() no-ops when a handler is unset (e.g. before the app mounts).
let onAuthExpired: (() => void) | null = null;
let onServerError: ((message: string) => void) | null = null;
export function setAuthExpiredHandler(fn: (() => void) | null): void {
  onAuthExpired = fn;
}
export function setServerErrorHandler(fn: ((message: string) => void) | null): void {
  onServerError = fn;
}

/** Apply the central policy to a non-ok response, then return the error to throw. */
function policyError(status: number, title: string, detail?: string): ApiError {
  // 401 only when we actually sent a token — a pre-auth probe (no token) 401s legitimately and must
  // not trigger the "session expired" flow. 403 is authorization (keep the user signed in).
  if (status === 401 && sessionToken) {
    onAuthExpired?.();
  } else if (status >= 500) {
    onServerError?.(detail || title || "Server error");
  }
  return new ApiError(status, title, detail);
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {
    "X-Vault-Client-Version": CLIENT_VERSION,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (sessionToken) headers["Authorization"] = `Bearer ${sessionToken}`;

  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    // Network-level failure (no HTTP response): the server is unreachable. Flag offline so the UI
    // can degrade gracefully, and normalize to ApiError(status 0) so callers' 401/403 checks
    // (which clear auth) never trigger on a mere connectivity drop.
    useConnectivity.getState().markApiFailure();
    throw new ApiError(0, "network", "Cannot reach the server");
  }
  // Got a response (even 4xx/5xx) → the server is reachable.
  useConnectivity.getState().markApiSuccess();

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const p = data as { title?: string; detail?: string } | undefined;
    throw policyError(res.status, p?.title ?? res.statusText, p?.detail);
  }
  return data as T;
}

export interface LoginResponse {
  session_token: string;
  expires_at?: string;
}
export interface Organization {
  id: string;
  name: string;
  wrapped_org_key: string; // base64
  is_personal?: boolean;
}
export interface ConnectorInfo {
  id: string;
  org_id: string;
  name: string;
  cn: string;
  status: "draft" | "online" | "offline" | "revoked";
  version: string;
  capabilities?: string[];
  last_seen?: string;
  created_at: string;
}
export interface ConnectorEnrollment {
  id: string;
  cn: string;
  leaf_cert_pem: string;
  leaf_key_pem: string;
  ca_pem: string;
  enroll_token: string;
  tunnel_host: string;
  bundle_password?: string;
  bundle_zip_b64?: string;
}
export interface ConnectorTask {
  id: string;
  type: string;
  target: string;
  status: "pending" | "running" | "done" | "error";
  parameters?: unknown;
  result?: unknown;
}
export interface DeviceInfo {
  id: string;
  name: string;
  platform: string;
  status: string;
  offline_enabled: boolean;
  keystore_backed: boolean;
}
export interface RecoveryStatus {
  opt_out: boolean;
  has_recovery: boolean;
  recovery_public_key?: string; // base64
  wrapped_org_key?: string; // base64
}
export interface GroupInfo {
  id: string;
  name: string;
  source: string;
  member_count: number;
}
export interface MeResponse {
  login: string;
  status: string;
  enrolled: boolean;
  is_break_glass: boolean;
  instance_capabilities: string[];
  public_key?: string;
  wrapped_user_key?: string;
  wrapped_private_key?: string;
  kdf?: { salt: string; mem_kib: number; time: number; parallelism: number };
}
export interface OidcConfigView {
  enabled: boolean;
  tenant_id: string;
  client_id: string;
  redirect_uri: string;
  has_certificate: boolean;
  cert_thumbprint?: string;
  cert_not_after?: string;
}
export interface CertResponse {
  certificate_pem: string;
  thumbprint: string;
  not_after: string;
}
export interface InstanceGrantView {
  principal_type: string;
  principal_id: string;
  name: string;
  role?: string;
  capabilities: string[];
  is_app_admin: boolean;
  grants_all_data: boolean;
  expires_at?: string;
}
export interface UserOrgAccess {
  org_id: string;
  name: string;
  has_grant: boolean;
  role?: string;
  capabilities: string[];
  authorized: boolean;
  can_decrypt: boolean;
}
export interface UserAccessView {
  user_id: string;
  name: string;
  is_app_admin: boolean;
  instance_role: string;
  instance_caps: string[];
  grants_all_data: boolean;
  orgs: UserOrgAccess[];
  groups: string[];
}
export interface OrgPrincipalAccess {
  principal_type: string;
  principal_id: string;
  name: string;
  role?: string;
  capabilities: string[];
  has_grant: boolean;
  grant_id?: string;
  can_decrypt: boolean;
}
/** One authorized-but-keyless pair from GET /admin/access/gaps (see vault/access-plan.ts). */
export interface AccessGap {
  org_id: string;
  org_name: string;
  user_id: string;
  login: string;
  enrolled: boolean;
  public_key?: string;
  via: string;
}
export interface AdminUser {
  id: string;
  login: string;
  auth_type: string;
  status: string;
  is_break_glass: boolean;
  sso_enabled: boolean;
  enrolled: boolean;
  public_key?: string;
  oidc_subject?: string;
}
export interface AuditEventRow {
  seq: number;
  id: string;
  event_type: string;
  result: string;
  created_at?: string;
  hash: string;
  signature: string;
  detail?: unknown;
}
export interface AuditReport {
  valid: boolean;
  events_checked: number;
  first_bad_seq?: number;
  reason?: string;
  head_hash_hex?: string;
}
export interface EnrollBody {
  public_key: string;
  wrapped_user_key: string;
  wrapped_private_key: string;
  kdf_salt: string;
  kdf_mem_kib: number;
  kdf_time: number;
  kdf_parallelism: number;
}
/** Change-master-password body (§7.2): only the re-wrapped User key + new KDF params. The
 *  private/org/item keys are unchanged, so they are not sent. */
export interface ChangeMasterBody {
  wrapped_user_key: string;
  kdf_salt: string;
  kdf_mem_kib: number;
  kdf_time: number;
  kdf_parallelism: number;
}
/** Per-org default behaviors (§11). */
export interface OrgSettings {
  default_offline_allowed: boolean;
  branding_name?: string; // white-label vault name shown in the header
  branding_logo?: string; // small data: URL (PNG/SVG)
}
export interface Folder {
  id: string;
  org_id: string;
  parent_folder_id?: string;
  enc_name: string; // base64
  node_kind: "folder" | "device";
  enc_meta?: string; // base64; device metadata (devices only)
  break_inheritance: boolean;
}
export interface ItemSummary {
  id: string;
  org_id: string;
  folder_id?: string;
  type: string;
  enc_content: string; // base64
  wrapped_item_key: string; // base64
  revision: number;
  offline_allowed?: boolean;
  updated_at?: string; // RFC3339; server-set on every write (used by the "modified" filter)
}
export interface Template {
  id: string;
  org_id: string;
  enc_name: string; // base64 ciphertext
  enc_meta: string; // base64 ciphertext (the encrypted template skeleton)
}
export interface ItemWriteBody {
  type: string;
  enc_content: string;
  wrapped_item_key: string;
  folder_id?: string;
  revision?: number;
  offline_allowed?: boolean;
}
export interface ItemVersion {
  version_no: number;
  enc_content: string; // base64
  created_at?: string;
}
export interface DirectoryUser {
  id: string;
  login: string;
  public_key: string; // base64
}
export interface SharedItem {
  id: string;
  item_id: string;
  type: string;
  wrapped_item_key: string; // base64, HPKE-wrapped to me
  enc_content: string; // base64
}
// One recipient an item is shared with (owner's view), incl. their pubkey for re-wrapping on revoke.
export interface ItemShareInfo {
  id: string;
  principal: string;
  login: string;
  public_key: string; // base64
  expires_at?: string;
}
export interface RevokeShareBody {
  revoke_share_id: string;
  revision: number;
  wrapped_item_key: string; // base64, the NEW item_key wrapped under the org key
  enc_content: string; // base64, item content re-encrypted under the new item_key
  remaining_shares: { share_id: string; wrapped_item_key: string }[];
}

// One-time secret sharing (burn-after-reading). Payload is E2E-encrypted client-side; the server only
// ever sees ciphertext + non-secret metadata. Meta/reveal are public (the recipient has no account).
export interface OneTimeShareMeta {
  status: "pending" | "consumed" | "expired" | "not_found";
  passphrase_required: boolean;
  label?: string;
  expires_at?: string;
}
export interface OneTimeShareReveal {
  ciphertext: string; // base64(nonce || AES-256-GCM)
  passphrase_required: boolean;
  label?: string;
}
export interface MyOneTimeShare {
  id: string;
  status: string;
  passphrase_required: boolean;
  label?: string;
  expires_at?: string;
  consumed_at?: string;
}

export const api = {
  health: () => request<{ status: string; version?: string }>("GET", "/healthz"),
  login: (login: string, password: string, totp?: string, clientId?: string, device?: string, platform?: string) =>
    request<LoginResponse>("POST", "/auth/login", {
      login,
      password,
      totp,
      client_id: clientId || undefined,
      device: device || undefined,
      platform: platform || undefined,
    }),
  // Exchange the caller's session for a NEW device-scoped session (browser extension pairing):
  // the extension gets its own device row + token independent of the SPA session.
  exchangeSession: (device: string, platform: string, clientId: string) =>
    request<LoginResponse>("POST", "/auth/sessions/exchange", { device, platform, client_id: clientId }),
  logout: () => request<void>("POST", "/auth/logout"),
  me: () => request<MeResponse>("GET", "/me"),
  enroll: (body: EnrollBody) => request<void>("POST", "/enroll", body),
  changeMaster: (body: ChangeMasterBody) => request<void>("POST", "/me/master", body),
  listOrgs: () => request<{ organizations: Organization[] }>("GET", "/orgs"),
  createOrg: (name: string, wrappedOrgKey: string) =>
    request<Organization>("POST", "/orgs", { name, wrapped_org_key: wrappedOrgKey }),
  createPersonalOrg: (wrappedOrgKey: string) =>
    request<Organization>("POST", "/orgs/personal", { wrapped_org_key: wrappedOrgKey }),
  listItems: (orgId: string, cursor?: string, updatedAfter?: string) =>
    request<{ items: ItemSummary[]; next_cursor?: string }>(
      "GET",
      `/orgs/${orgId}/items?limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""}${
        updatedAfter ? `&updated_after=${encodeURIComponent(updatedAfter)}` : ""
      }`,
    ),
  // Lane B (jump server): mint a short-lived, single-use connection token for one target item.
  // Enforces LaunchSession server-side and audits session_started; returns only the opaque token.
  // `connectorId` optionally routes the session through an outbound connector (item override).
  mintConnectionToken: (itemId: string, connectorId?: string) =>
    request<{ token: string; channel: "ssh" | "rdp"; expires_at: string }>(
      "POST",
      `/items/${itemId}/connection-token`,
      connectorId ? { connector_id: connectorId } : {},
    ),
  // Outbound connectors (jump server Phase 2) — admin management.
  listConnectors: (orgId: string) =>
    request<{ connectors: ConnectorInfo[] }>("GET", `/admin/connectors?org_id=${encodeURIComponent(orgId)}`),
  createConnector: (orgId: string, name: string) =>
    request<ConnectorEnrollment>("POST", "/admin/connectors", { org_id: orgId, name }),
  revokeConnector: (id: string) => request<void>("DELETE", `/admin/connectors/${id}`),
  listConnectorTasks: (id: string) => request<{ tasks: ConnectorTask[] }>("GET", `/admin/connectors/${id}/tasks`),
  createConnectorTask: (id: string, type: string, target?: string, parameters?: unknown) =>
    request<ConnectorTask>("POST", `/admin/connectors/${id}/tasks`, { type, target: target ?? "", parameters }),
  listFolders: (orgId: string) => request<{ folders: Folder[] }>("GET", `/orgs/${orgId}/folders`),
  orgCapabilities: (orgId: string) => request<{ capabilities: string[] }>("GET", `/orgs/${orgId}/capabilities`),
  getOrgSettings: (orgId: string) => request<OrgSettings>("GET", `/orgs/${orgId}/settings`),
  putOrgSettings: (orgId: string, settings: OrgSettings) => request<OrgSettings>("PUT", `/orgs/${orgId}/settings`, settings),
  // Devices + offline cache (§15)
  getSelfDevice: () => request<DeviceInfo>("GET", "/devices/self"),
  patchSelfDevice: (offlineEnabled: boolean, keystoreBacked: boolean) =>
    request<DeviceInfo>("PATCH", "/devices/self", { offline_enabled: offlineEnabled, keystore_backed: keystoreBacked }),
  // Recovery (§17.1)
  setRecovery: (orgId: string, body: { opt_out: boolean; recovery_public_key?: string; wrapped_org_key?: string }) =>
    request<void>("POST", `/orgs/${orgId}/recovery`, body),
  getRecovery: (orgId: string) => request<RecoveryStatus>("GET", `/orgs/${orgId}/recovery`),
  // Groups (§9)
  listGroups: () => request<{ groups: GroupInfo[] }>("GET", "/admin/groups"),
  createGroup: (name: string) => request<GroupInfo>("POST", "/admin/groups", { name }),
  deleteGroup: (id: string) => request<void>("DELETE", `/admin/groups/${id}`),
  listGroupMembers: (id: string) => request<{ members: { user_id: string; login: string }[] }>("GET", `/admin/groups/${id}/members`),
  addGroupMember: (id: string, userId: string) => request<void>("POST", `/admin/groups/${id}/members`, { user_id: userId }),
  removeGroupMember: (id: string, userId: string) => request<void>("DELETE", `/admin/groups/${id}/members/${userId}`),
  // Client audit events (§16)
  recordEvent: (body: { event_type: string; item_id?: string; detail?: string; reason?: string }) =>
    request<void>("POST", "/events", body),
  exportOrg: (orgId: string) => request<{ ok: boolean }>("POST", `/orgs/${orgId}/export`),
  createFolder: (orgId: string, encName: string, parentFolderId?: string, nodeKind?: "folder" | "device", encMeta?: string) =>
    request<Folder>("POST", `/orgs/${orgId}/folders`, {
      enc_name: encName,
      parent_folder_id: parentFolderId,
      node_kind: nodeKind,
      enc_meta: encMeta,
    }),
  updateFolder: (id: string, encName: string, encMeta?: string) =>
    request<Folder>("PATCH", `/folders/${id}`, { enc_name: encName, enc_meta: encMeta }),
  moveFolder: (id: string, parentFolderId?: string) =>
    request<Folder>("POST", `/folders/${id}/move`, { parent_folder_id: parentFolderId ?? "" }),
  deleteFolder: (id: string) => request<void>("DELETE", `/folders/${id}`),

  // Per-org entry templates (UX phase 3). enc_name/enc_meta are client-encrypted ciphertext (§19).
  listTemplates: (orgId: string) => request<{ templates: Template[] }>("GET", `/orgs/${orgId}/templates`),
  createTemplate: (orgId: string, encName: string, encMeta: string) =>
    request<Template>("POST", `/orgs/${orgId}/templates`, { enc_name: encName, enc_meta: encMeta }),
  updateTemplate: (id: string, encName: string, encMeta: string) =>
    request<Template>("PATCH", `/templates/${id}`, { enc_name: encName, enc_meta: encMeta }),
  deleteTemplate: (id: string) => request<void>("DELETE", `/templates/${id}`),
  moveItem: (id: string, folderId?: string) => request<void>("POST", `/items/${id}/move`, { folder_id: folderId ?? "" }),
  getItem: (id: string) => request<ItemSummary>("GET", `/items/${id}`),
  createItem: (orgId: string, body: ItemWriteBody) => request<ItemSummary>("POST", `/orgs/${orgId}/items`, body),
  updateItem: (id: string, body: ItemWriteBody) => request<ItemSummary>("PUT", `/items/${id}`, body),
  deleteItem: (id: string) => request<void>("DELETE", `/items/${id}`),
  // Versions + trash (§26)
  listVersions: (id: string) => request<{ versions: ItemVersion[] }>("GET", `/items/${id}/versions`),
  restoreVersion: (id: string, versionNo: number) => request<ItemSummary>("POST", `/items/${id}/versions/${versionNo}/restore`),
  listTrash: (orgId: string) => request<{ items: ItemSummary[] }>("GET", `/orgs/${orgId}/trash`),
  restoreItem: (id: string) => request<void>("POST", `/items/${id}/restore`),
  addMember: (orgId: string, userId: string, wrappedOrgKey: string) =>
    request<void>("POST", `/orgs/${orgId}/members`, { user_id: userId, wrapped_org_key: wrappedOrgKey }),

  // Item-share (§12)
  directory: () => request<{ users: DirectoryUser[] }>("GET", "/directory"),
  createShare: (itemId: string, recipientUserId: string, wrappedItemKey: string) =>
    request<{ id: string }>("POST", `/items/${itemId}/shares`, { recipient_user_id: recipientUserId, wrapped_item_key: wrappedItemKey }),
  listShares: () => request<{ shares: SharedItem[] }>("GET", "/shares"),
  // Who an item is shared with (with recipient pubkeys), for the owner to review + revoke.
  listItemShares: (itemId: string) => request<{ shares: ItemShareInfo[] }>("GET", `/items/${itemId}/shares`),
  // Revoke a share: the server deletes it AND rotates the item_key (§7.2/§12). The client supplies
  // the re-encrypted item + the item_key re-wrapped for the org and every remaining recipient.
  revokeShare: (itemId: string, body: RevokeShareBody) =>
    request<void>("POST", `/items/${itemId}/shares/revoke`, body),

  // One-time secret sharing (burn-after-reading). createOneTimeShare is authenticated; meta/reveal are
  // public (no token) — the recipient never signs in. reveal is single-use and consumes the link.
  createOneTimeShare: (body: { ciphertext: string; passphrase_required: boolean; label?: string; ttl_seconds?: number }) =>
    request<{ id: string }>("POST", "/secret-links", body),
  getOneTimeShareMeta: (id: string) => request<OneTimeShareMeta>("GET", `/secret-links/${id}`),
  revealOneTimeShare: (id: string) => request<OneTimeShareReveal>("POST", `/secret-links/${id}/reveal`),
  listMyOneTimeShares: () => request<{ links: MyOneTimeShare[] }>("GET", "/secret-links/mine"),
  deleteOneTimeShare: (id: string) => request<void>("DELETE", `/secret-links/${id}`),

  // Admin
  getOidcConfig: () => request<OidcConfigView>("GET", "/admin/oidc/config"),
  putOidcConfig: (body: { enabled: boolean; tenant_id: string; client_id: string; redirect_uri: string }) =>
    request<OidcConfigView>("PUT", "/admin/oidc/config", body),
  getCert: () => request<CertResponse>("GET", "/admin/oidc/certificate"),
  generateCert: () => request<CertResponse>("POST", "/admin/oidc/certificate"),
  listUsers: () => request<{ users: AdminUser[] }>("GET", "/admin/users"),
  createUser: (login: string, ssoEnabled: boolean) =>
    request<{ id: string; login: string }>("POST", "/admin/users", { login, sso_enabled: ssoEnabled }),
  setUserSso: (id: string, ssoEnabled: boolean) =>
    request<void>("PATCH", `/admin/users/${id}/sso`, { sso_enabled: ssoEnabled }),
  createGrant: (body: {
    principal_type: string;
    principal_id: string;
    scope: string;
    node_id?: string;
    role?: string;
    capabilities?: string[];
  }) => request<{ id: string; scope: string }>("POST", "/admin/grants", body),
  // Access visibility (admin): who has access to what / who can access everything.
  getAccessOverview: () =>
    request<{ instance_grants: InstanceGrantView[]; break_glass: { id: string; name: string }[] }>("GET", "/admin/access/overview"),
  getUserAccess: (userId: string) => request<UserAccessView>("GET", `/admin/users/${userId}/access`),
  // Authorized but holds no org key — the work list the admin's browser drains by re-wrapping.
  getAccessGaps: () => request<{ gaps: AccessGap[] }>("GET", "/admin/access/gaps"),
  listAllOrgs: () => request<{ organizations: { id: string; name: string }[] }>("GET", "/admin/orgs"),
  getOrgAccess: (orgId: string) => request<{ org_id: string; principals: OrgPrincipalAccess[] }>("GET", `/admin/orgs/${orgId}/access`),
  deleteGrant: (grantId: string) => request<void>("DELETE", `/admin/grants/${grantId}`),
  revokeInstanceGrant: (principalId: string, principalType = "user") =>
    request<void>("DELETE", `/admin/instance-grants/${principalId}?principal_type=${principalType}`),
  auditSeal: () => request<{ sealed: number; head_hash: string }>("POST", "/admin/audit/seal"),
  auditVerify: () => request<AuditReport>("GET", "/admin/audit/verify"),
  auditHead: () => request<{ seq: number; head_hash: string }>("GET", "/admin/audit/head"),
  auditExport: () => request<{ events: AuditEventRow[] }>("GET", "/admin/audit/export"),
  // Attachments (§14)
  createAttachment: (itemId: string, body: AttachmentMeta) => request<{ id: string }>("POST", `/items/${itemId}/attachments`, body),
  listAttachments: (itemId: string) => request<{ attachments: AttachmentRow[] }>("GET", `/items/${itemId}/attachments`),
  deleteAttachment: (id: string) => request<void>("DELETE", `/attachments/${id}`),
  putAttachmentBlob: (id: string, body: Uint8Array) => putBinary(`/attachments/${id}/blob`, body),
  getAttachmentBlob: (id: string) => getBinary(`/attachments/${id}/blob`),

  oidcLoginUrl: (clientId?: string) =>
    clientId ? `${BASE}/auth/oidc/login?client_id=${encodeURIComponent(clientId)}` : `${BASE}/auth/oidc/login`,
  claimOidc: () => request<{ session_token?: string }>("GET", "/auth/oidc/claim"),
};

export interface AttachmentMeta {
  enc_filename: string;
  enc_content_type?: string;
  size_bytes: number;
  chunk_count: number;
  wrapped_attachment_key: string;
  enc_content_hash: string;
}
export interface AttachmentRow extends AttachmentMeta {
  id: string;
}

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { "X-Vault-Client-Version": CLIENT_VERSION };
  if (sessionToken) h["Authorization"] = `Bearer ${sessionToken}`;
  return h;
}

async function putBinary(path: string, body: Uint8Array): Promise<void> {
  const res = await fetch(BASE + path, {
    method: "PUT",
    headers: { ...authHeaders(), "Content-Type": "application/octet-stream" },
    body: body as BodyInit,
  });
  if (!res.ok) throw policyError(res.status, "Upload failed");
}

async function getBinary(path: string): Promise<Uint8Array> {
  const res = await fetch(BASE + path, { headers: authHeaders() });
  if (!res.ok) throw policyError(res.status, "Download failed");
  return new Uint8Array(await res.arrayBuffer());
}
